import { useState, useEffect } from 'react';
import { getApiUrl } from '../../config';
import { apiRequest } from '../../utils/apiUtils';

export interface TimeBlock {
  id?: string;
  startTime: string; // "09:00"
  endTime: string; // "22:00"
  isPeakHour: boolean;
}

export interface DayAvailability {
  dayOfWeek: 0 | 1 | 2 | 3 | 4 | 5 | 6; // 0 = Sunday, 6 = Saturday
  enabled: boolean;
  blocks: TimeBlock[];
}

interface VenueAvailabilityResponse {
  id: string;
  venueId: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isPeakHour: boolean;
}

export const useVenueAvailability = (venueId: string | undefined) => {
  const [availability, setAvailability] = useState<DayAvailability[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize with empty 7 days
  const initializeEmptyAvailability = (): DayAvailability[] => {
    return Array.from({ length: 7 }, (_, i) => ({
      dayOfWeek: i as DayAvailability['dayOfWeek'],
      enabled: false,
      blocks: [],
    }));
  };

  // Fetch availability from backend
  useEffect(() => {
    if (!venueId) {
      setIsLoading(false);
      return;
    }

    const fetchAvailability = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const result = await apiRequest<{ availability: VenueAvailabilityResponse[]; bookings: any[] }>(
          `/api/venues/${venueId}/availability`,
          { requireAuth: true }
        );

        if (result.data?.availability) {
          // Convert API response to DayAvailability format
          const daysMap = new Map<number, TimeBlock[]>();

          result.data.availability.forEach((item) => {
            if (!daysMap.has(item.dayOfWeek)) {
              daysMap.set(item.dayOfWeek, []);
            }
            daysMap.get(item.dayOfWeek)!.push({
              id: item.id,
              startTime: item.startTime,
              endTime: item.endTime,
              isPeakHour: item.isPeakHour,
            });
          });

          // Create array with all days
          const availabilityData = initializeEmptyAvailability();
          daysMap.forEach((blocks, dayOfWeek) => {
            availabilityData[dayOfWeek] = {
              dayOfWeek: dayOfWeek as DayAvailability['dayOfWeek'],
              enabled: blocks.length > 0,
              blocks,
            };
          });

          setAvailability(availabilityData);
        } else {
          // No availability yet, initialize empty
          setAvailability(initializeEmptyAvailability());
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching availability:', err);
        setError('Error al cargar la disponibilidad');
        setAvailability(initializeEmptyAvailability());
      } finally {
        setIsLoading(false);
      }
    };

    fetchAvailability();
  }, [venueId]);

  // Save availability to backend
  const saveAvailability = async (): Promise<boolean> => {
    if (!venueId) return false;

    try {
      setIsSaving(true);
      setError(null);

      // Convert DayAvailability to API format
      const availabilityPayload: Array<{
        dayOfWeek: number;
        startTime: string;
        endTime: string;
        isPeakHour: boolean;
      }> = [];

      availability.forEach((day) => {
        if (day.enabled && day.blocks.length > 0) {
          day.blocks.forEach((block) => {
            availabilityPayload.push({
              dayOfWeek: day.dayOfWeek,
              startTime: block.startTime,
              endTime: block.endTime,
              isPeakHour: block.isPeakHour,
            });
          });
        }
      });

      const result = await apiRequest(
        `/api/venues/${venueId}/availability`,
        {
          method: 'POST',
          body: { availability: availabilityPayload },
          requireAuth: true,
        }
      );

      if (result.error) {
        setError(result.error);
        return false;
      }

      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error saving availability:', err);
      setError('Error al guardar la disponibilidad');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Helper: Toggle day enabled/disabled
  const toggleDay = (dayOfWeek: number) => {
    setAvailability((prev) =>
      prev.map((day) =>
        day.dayOfWeek === dayOfWeek
          ? {
              ...day,
              enabled: !day.enabled,
              // If enabling and no blocks, add default block
              blocks: !day.enabled && day.blocks.length === 0
                ? [{ startTime: '09:00', endTime: '22:00', isPeakHour: false }]
                : day.blocks
            }
          : day
      )
    );
  };

  // Helper: Add new time block to day
  const addBlock = (dayOfWeek: number) => {
    setAvailability((prev) =>
      prev.map((day) =>
        day.dayOfWeek === dayOfWeek
          ? {
              ...day,
              blocks: [
                ...day.blocks,
                { startTime: '09:00', endTime: '22:00', isPeakHour: false },
              ],
            }
          : day
      )
    );
  };

  // Helper: Remove time block from day
  const removeBlock = (dayOfWeek: number, blockIndex: number) => {
    setAvailability((prev) =>
      prev.map((day) =>
        day.dayOfWeek === dayOfWeek
          ? {
              ...day,
              blocks: day.blocks.filter((_, i) => i !== blockIndex),
            }
          : day
      )
    );
  };

  // Helper: Update time block field
  const updateBlock = (
    dayOfWeek: number,
    blockIndex: number,
    field: 'startTime' | 'endTime' | 'isPeakHour',
    value: any
  ) => {
    setAvailability((prev) =>
      prev.map((day) =>
        day.dayOfWeek === dayOfWeek
          ? {
              ...day,
              blocks: day.blocks.map((block, i) =>
                i === blockIndex ? { ...block, [field]: value } : block
              ),
            }
          : day
      )
    );
  };

  // Validation helper
  const validateAvailability = (): string | null => {
    // At least one day must be enabled
    const hasEnabledDay = availability.some((day) => day.enabled && day.blocks.length > 0);
    if (!hasEnabledDay) {
      return 'Debes habilitar al menos un día con horarios disponibles';
    }

    // Validate each enabled day
    for (const day of availability) {
      if (!day.enabled) continue;

      for (const block of day.blocks) {
        // Validate time range
        if (block.startTime >= block.endTime) {
          return `Error en ${getDayName(day.dayOfWeek)}: La hora de fin debe ser posterior a la hora de inicio`;
        }

        // Validate minimum duration (1 hour)
        const start = parseInt(block.startTime.split(':')[0]);
        const end = parseInt(block.endTime.split(':')[0]);
        if (end - start < 1) {
          return `Error en ${getDayName(day.dayOfWeek)}: El bloque debe tener al menos 1 hora de duración`;
        }
      }

      // Check for overlapping blocks
      for (let i = 0; i < day.blocks.length; i++) {
        for (let j = i + 1; j < day.blocks.length; j++) {
          const block1 = day.blocks[i];
          const block2 = day.blocks[j];

          if (
            (block1.startTime < block2.endTime && block1.endTime > block2.startTime) ||
            (block2.startTime < block1.endTime && block2.endTime > block1.startTime)
          ) {
            return `Error en ${getDayName(day.dayOfWeek)}: Los bloques horarios se superponen`;
          }
        }
      }
    }

    return null;
  };

  return {
    availability,
    isLoading,
    isSaving,
    error,
    saveAvailability,
    toggleDay,
    addBlock,
    removeBlock,
    updateBlock,
    validateAvailability,
  };
};

// Helper function to get day name in Spanish
export const getDayName = (dayOfWeek: number): string => {
  const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  return days[dayOfWeek] || '';
};
